import React from 'react';
import { Crosshair, Flame, FlaskConical, Cpu, Target, Activity } from 'lucide-react';

export const SHIFTS_2026 = [
  { id: '11_1', label: '11 April - Shift 1', diffOffset: 1.2, difficulty: 'Moderate', diffPct: 55, bonus: 0 },
  { id: '11_2', label: '11 April - Shift 2', diffOffset: -0.5, difficulty: 'Easy to Moderate', diffPct: 40, bonus: 0 },
  { id: '13_1', label: '13 April - Shift 1', diffOffset: 0.0, difficulty: 'Moderate', diffPct: 50, bonus: 0 },
  { id: '13_2', label: '13 April - Shift 2', diffOffset: 2.1, difficulty: 'Hard', diffPct: 80, bonus: 0 },
  { id: '15_1', label: '15 April - Shift 1', diffOffset: 3.5, difficulty: 'Toughest Shift', diffPct: 98, bonus: 2, bonusMsg: 'Math Q-ID Error (Repeated Options)' },
  { id: '15_2', label: '15 April - Shift 2', diffOffset: 0.8, difficulty: 'Moderate', diffPct: 60, bonus: 0 },
  { id: '16_1', label: '16 April - Shift 1', diffOffset: -0.2, difficulty: 'Easy to Moderate', diffPct: 45, bonus: 0 },
  { id: '16_2', label: '16 April - Shift 2', diffOffset: 1.5, difficulty: 'Moderate to Hard', diffPct: 70, bonus: 0 },
  { id: '17_1', label: '17 April - Shift 1', diffOffset: -3.0, difficulty: 'Easiest Shift', diffPct: 15, bonus: 0 },
  { id: '17_2', label: '17 April - Shift 2', diffOffset: -0.8, difficulty: 'Moderate', diffPct: 40, bonus: 0 },
  { id: '18_1', label: '18 April - Shift 1', diffOffset: 2.5, difficulty: 'Tricky / Time Consuming', diffPct: 85, bonus: 0 },
  { id: '18_2', label: '18 April - Shift 2', diffOffset: 0.0, difficulty: 'Moderate', diffPct: 50, bonus: 0 },
  { id: '19_1', label: '19 April - Shift 1', diffOffset: 0.5, difficulty: 'Moderate', diffPct: 55, bonus: 0 },
  { id: '19_2', label: '19 April - Shift 2', diffOffset: -0.3, difficulty: 'Easy', diffPct: 35, bonus: 0 },
  { id: '20_1', label: '20 April - Shift 1', diffOffset: -1.0, difficulty: 'Easy to Moderate', diffPct: 30, bonus: 0 },
  { id: '20_2', label: '20 April - Shift 2', diffOffset: 1.0, difficulty: 'Moderate', diffPct: 65, bonus: 0 }
];

export const predictTotalPercentile = (score, shiftOffset) => {
  const n = (score || 0) + (shiftOffset || 0);
  if (n >= 165) return 99.91; if (n >= 145) return 99.45 + ((n - 145) * 0.02);
  if (n >= 125) return 98.00 + ((n - 125) * 0.07); if (n >= 105) return 95.50 + ((n - 105) * 0.12);
  if (n >= 85)  return 90.00 + ((n - 85) * 0.25); if (n >= 65)  return 80.00 + ((n - 65) * 0.50);
  if (n >= 45)  return 60.00 + ((n - 45) * 1.00); return Math.max(5, n * 1.2); 
};

export const predictRank = (percentile) => Math.round(((100 - (percentile || 0)) / 100) * 320000) || 1;

export const predictSubjectPercentile = (sub, marks, offset) => {
  const n = (marks || 0) + ((offset || 0) / 3); let p = 0;
  if (sub === 'math') {
    if (n >= 85) p = 99.5 + (n-85)*0.03; else if (n >= 70) p = 97.0 + (n-70)*0.15;
    else if (n >= 50) p = 90.0 + (n-50)*0.35; else p = n * 1.5;
  } else {
    if (n >= 42) p = 99.5 + (n-42)*0.06; else if (n >= 35) p = 97.0 + (n-35)*0.35;
    else if (n >= 25) p = 90.0 + (n-25)*0.70; else p = n * 2.5;
  }
  return Math.min(99.99, Math.max(5, p));
};

const decodeQuotedPrintable = (value = '') => {
  const compact = value.replace(/=\r?\n/g, '');
  const percentEncoded = compact.replace(/=([a-fA-F0-9]{2})/g, '%$1');
  try {
    return decodeURIComponent(percentEncoded);
  } catch (_) {
    return compact.replace(/=([a-fA-F0-9]{2})/g, (m, p1) => String.fromCharCode(parseInt(p1, 16)));
  }
};

const decodeBase64Text = (value = '') => {
  try {
    const binary = atob(value.replace(/\s/g, ''));
    if (typeof TextDecoder !== 'undefined') {
      const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
      return new TextDecoder('utf-8').decode(bytes);
    }
    return decodeURIComponent(escape(binary));
  } catch (_) {
    return value;
  }
};

const decodeHtmlEntities = (value = '') => {
  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  return value
    .replace(/&#(\d+);/g, (m, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([a-fA-F0-9]+);/g, (m, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
};

const extractBodyFromMimePart = (part = '') => {
  const split = part.match(/\r?\n\r?\n/);
  if (!split) return { headers: part, body: part };
  const splitAt = split.index + split[0].length;
  return {
    headers: part.slice(0, split.index).toLowerCase(),
    body: part.slice(splitAt).trim()
  };
};

const decodeMimeBody = (headers = '', body = '') => {
  const normalizedHeaders = headers.toLowerCase();
  if (/content-transfer-encoding:\s*base64/i.test(normalizedHeaders)) return decodeBase64Text(body);
  if (/content-transfer-encoding:\s*quoted-printable/i.test(normalizedHeaders)) return decodeQuotedPrintable(body);
  return body;
};

const getMimeBoundary = (headersAndPreamble = '') => {
  const boundaryMatch = headersAndPreamble.match(/boundary=(?:"([^"]+)"|([^;\r\n]+))/i);
  return boundaryMatch ? (boundaryMatch[1] || boundaryMatch[2] || '').trim() : '';
};

const splitMimeParts = (content = '', boundary = '') => {
  if (!boundary) return [];
  const escapedBoundary = boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return content
    .split(new RegExp(`(?:^|\\r?\\n)--${escapedBoundary}(?:--)?\\s*(?:\\r?\\n|$)`))
    .map(part => part.trim())
    .filter(Boolean);
};

const looksLikeResponseSheet = (value = '') => (
  /Correct\s*(?:Option|Answer|Response|Ans)/i.test(value) &&
  /(?:Candidate|Chosen|Your|Selected|Marked)\s*(?:Response|Answer|Option)/i.test(value)
);

const extractHtmlDocument = (value = '') => {
  const match = value.match(/<!doctype[\s\S]*?<\/html>|<html[\s\S]*?<\/html>/i);
  return match ? match[0] : '';
};

const decodeMimeArchive = (content = '', depth = 0) => {
  if (depth > 4) return content;

  const boundary = getMimeBoundary(content);
  if (!boundary) return decodeQuotedPrintable(content);

  const parts = splitMimeParts(content, boundary).map(part => {
    const { headers, body } = extractBodyFromMimePart(part);
    const decodedBody = decodeMimeBody(headers, body);
    const isNestedMime = /multipart\/(?:related|mixed|alternative)/i.test(headers) || /MIME-Version:|multipart\/(?:related|mixed|alternative)/i.test(decodedBody);

    return {
      headers,
      body: isNestedMime ? decodeMimeArchive(decodedBody, depth + 1) : decodedBody
    };
  });

  const responsePart = parts.find(part => looksLikeResponseSheet(part.body));
  if (responsePart) return responsePart.body;

  const htmlPart = parts.find(part => /content-type:\s*text\/html/i.test(part.headers));
  if (htmlPart) return htmlPart.body;

  const htmlBody = parts.map(part => extractHtmlDocument(part.body)).find(Boolean);
  if (htmlBody) return htmlBody;

  const textPart = parts.find(part => /content-type:\s*text\/plain/i.test(part.headers));
  if (textPart) return textPart.body;

  return parts.map(part => part.body).join(' ');
};

const extractReadableContent = (rawContent = '') => {
  let content = rawContent.replace(/\0/g, '');
  const looksLikeMimeArchive = /MIME-Version:|multipart\/(?:related|mixed|alternative)/i.test(content);

  if (!looksLikeMimeArchive) {
    return decodeQuotedPrintable(content);
  }

  const decodedArchive = decodeMimeArchive(content);
  if (looksLikeResponseSheet(decodedArchive)) return decodedArchive;

  const embeddedHtml = extractHtmlDocument(content);
  if (embeddedHtml && looksLikeResponseSheet(embeddedHtml)) return embeddedHtml;

  return decodedArchive;
};

const normalizeAnswerValue = (value = '') => {
  const trimmed = value.trim().toUpperCase();
  const letterToOption = { A: '1', B: '2', C: '3', D: '4' };
  return letterToOption[trimmed] || trimmed;
};

const uniqueInOrder = (values = []) => {
  const seen = new Set();
  return values.filter(value => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const extractLikelyOptionIds = (value = '') => {
  const numericTokens = [...value.matchAll(/\b\d{5,}\b/g)].map(match => match[0]);
  const counts = numericTokens.reduce((acc, token) => {
    acc[token] = (acc[token] || 0) + 1;
    return acc;
  }, {});

  const distinctCandidates = uniqueInOrder(numericTokens.filter(token => counts[token] <= 3));
  const clusteredOptions = distinctCandidates.reduce((groups, token, index) => {
    const key = token.slice(0, 3);
    const group = groups[key] || { values: [], lastIndex: index };
    group.values.push(token);
    group.lastIndex = index;
    groups[key] = group;
    return groups;
  }, {});

  const optionGroup = Object.values(clusteredOptions)
    .filter(group => group.values.length >= 4)
    .sort((a, b) => b.lastIndex - a.lastIndex)[0];

  return (optionGroup ? optionGroup.values : distinctCandidates).slice(-4);
};

const extractAnswerAfterLabel = (text = '', labelPattern, optionIds = [], fallback = '') => {
  const labelMatch = text.match(labelPattern);
  if (!labelMatch) return normalizeAnswerValue(fallback);

  const afterLabel = text.slice(labelMatch.index + labelMatch[0].length);
  const answerTokens = [...afterLabel.matchAll(/\b([A-D]|\d+)\b/gi)].map(match => normalizeAnswerValue(match[1]));
  const optionSet = new Set(optionIds.map(normalizeAnswerValue));
  const matchedOption = answerTokens.find(token => optionSet.has(token));

  return matchedOption || answerTokens[0] || normalizeAnswerValue(fallback);
};

export const parseResponseSheet = (rawContent) => {
  const extractedHtml = extractReadableContent(rawContent || '');

  // 2. Deep HTML Clean
  const cleanText = decodeHtmlEntities(extractedHtml)
    .replace(/[\u200B-\u200D\uFEFF]/g, '')  
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '') 
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '') 
    .replace(/<\/(?:tr|p|div|li|h[1-6])>/gi, ' ')
    .replace(/<\/(?:td|th)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[|,;\t]+/g, ' ')
    .replace(/[-_]{2,}/g, ' ')
    .replace(/\s+/g, ' ')                   
    .trim();

  const scores = { total: 0, physics: { marks: 0, correct: 0, incorrect: 0, skipped: 0, totalQs: 0 }, chemistry: { marks: 0, correct: 0, incorrect: 0, skipped: 0, totalQs: 0 }, math: { marks: 0, correct: 0, incorrect: 0, skipped: 0, totalQs: 0 }, totalParsedQuestions: 0 };
  
  const answerToken = '([A-D]|\\d+)';
  const correctLabel = `\\b(?:Correct\\s*(?:Option|Answer|Response|Ans)|Answer\\s*Key|Key\\s*Answer|Correct\\s*Choice)\\b\\s*(?:No\\.?|Option|Choice)?\\s*(?:[:=\\-]|is)?\\s*`;
  const candidateLabel = `\\b(?:Candidate\\s*(?:Response|Answer|Option)|Chosen\\s*(?:Option|Answer|Response)|Your\\s*(?:Response|Answer|Option)|Selected\\s*(?:Option|Answer|Response)|Marked\\s*(?:Option|Answer|Response))\\b\\s*(?:No\\.?|Option|Choice)?\\s*(?:[:=\\-]|is)?\\s*`;
  const correctPattern = new RegExp(`${correctLabel}${answerToken}?\\b`, 'gi');
  const correctLabelPattern = new RegExp(correctLabel, 'i');
  const candidatePattern = new RegExp(`${candidateLabel}${answerToken}?\\b`, 'i');
  const candidateLabelPattern = new RegExp(candidateLabel, 'i');
  const skippedPattern = /\b(?:not\s*answered|not\s*attempted|unanswered|blank|--|n\/a)\b/i;
  const matches = [...cleanText.matchAll(correctPattern)];
  scores.totalParsedQuestions = matches.length;

  matches.forEach((match, index) => {
    const previousMatch = matches[index - 1];
    const startIndex = match.index;
    let subject = 'physics'; let weightage = 1;
    
    if (index >= 100) { subject = 'math'; weightage = 2; } 
    else if (index >= 50) { subject = 'chemistry'; }
    scores[subject].totalQs += 1;
    
    const nextMatch = matches[index + 1];
    const questionChunk = cleanText.slice(startIndex, nextMatch ? nextMatch.index : cleanText.length);
    const optionSource = cleanText.slice(previousMatch ? previousMatch.index + previousMatch[0].length : 0, startIndex);
    const optionIds = extractLikelyOptionIds(optionSource);
    const candidateLabelMatch = questionChunk.match(candidateLabelPattern);
    const correctChunk = candidateLabelMatch ? questionChunk.slice(0, candidateLabelMatch.index) : questionChunk;
    const correctId = extractAnswerAfterLabel(correctChunk, correctLabelPattern, optionIds, match[1]);
    const candidateMatch = questionChunk.match(candidatePattern);
    
    if (candidateMatch) {
      const candidateId = extractAnswerAfterLabel(questionChunk.slice(candidateMatch.index), candidateLabelPattern, optionIds, candidateMatch[1]);
      if (candidateId === correctId) { scores[subject].correct += 1; scores[subject].marks += weightage; scores.total += weightage; } 
      else { scores[subject].incorrect += 1; }
    } else if (skippedPattern.test(questionChunk)) {
      scores[subject].skipped += 1;
    } else { scores[subject].skipped += 1; }
  });
  return scores;
};

export const generateDeepCapColleges = (rank, category, branch, location) => {
  let effRank = rank || 1;
  if (category === 'OBC' || category === 'EWS') effRank *= 0.75;
  if (category === 'SC') effRank *= 0.35;
  if (category === 'ST') effRank *= 0.20;

  const allColleges = [
    { name: 'VJTI, Mumbai', loc: 'Mumbai', cutoff: 300, seats: 12 }, 
    { name: 'COEP, Pune', loc: 'Pune', cutoff: 450, seats: 14 },
    { name: 'SPIT, Mumbai', loc: 'Mumbai', cutoff: 1200, seats: 20 }, 
    { name: 'PICT, Pune', loc: 'Pune', cutoff: 1500, seats: 24 },
    { name: 'Walchand, Sangli', loc: 'Other', cutoff: 3500, seats: 30 },
    { name: 'VIT, Pune', loc: 'Pune', cutoff: 4000, seats: 45 },
    { name: 'DJ Sanghvi, Mumbai', loc: 'Mumbai', cutoff: 4200, seats: 35 }, 
    { name: 'PCCOE, Pune', loc: 'Pune', cutoff: 7500, seats: 60 },
    { name: 'KJSCE (Somaiya), Mumbai', loc: 'Mumbai', cutoff: 8000, seats: 40 },
    { name: 'Cummins, Pune (W)', loc: 'Pune', cutoff: 9000, seats: 40 }, 
    { name: 'Thadomal Shahani, Mumbai', loc: 'Mumbai', cutoff: 11000, seats: 50 },
    { name: 'VESIT, Mumbai', loc: 'Mumbai', cutoff: 12000, seats: 45 },
    { name: 'Ramdeobaba, Nagpur', loc: 'Other', cutoff: 14000, seats: 50 },
    { name: 'VIIT, Pune', loc: 'Pune', cutoff: 15000, seats: 60 },
    { name: 'Fr. Agnel, Bandra', loc: 'Mumbai', cutoff: 16000, seats: 40 },
    { name: 'Thakur, Mumbai', loc: 'Mumbai', cutoff: 18000, seats: 90 },
    { name: 'AISSMS, Pune', loc: 'Pune', cutoff: 22000, seats: 120 }, 
    { name: 'DY Patil (Akurdi), Pune', loc: 'Pune', cutoff: 25000, seats: 100 },
    { name: 'YCCE, Nagpur', loc: 'Other', cutoff: 28000, seats: 90 },
    { name: 'Sinhgad (Vadgaon), Pune', loc: 'Pune', cutoff: 35000, seats: 180 }, 
    { name: 'RGIT, Mumbai', loc: 'Mumbai', cutoff: 38000, seats: 100 },
    { name: 'DY Patil (Pimpri), Pune', loc: 'Pune', cutoff: 42000, seats: 150 }, 
    { name: 'Don Bosco, Mumbai', loc: 'Mumbai', cutoff: 45000, seats: 80 },
    { name: 'GCE, Amravati', loc: 'Other', cutoff: 48000, seats: 60 },
    { name: 'JSPM, Pune', loc: 'Pune', cutoff: 55000, seats: 200 }, 
    { name: 'Datta Meghe, Navi Mumbai', loc: 'Mumbai', cutoff: 65000, seats: 150 }, 
    { name: 'G.H. Raisoni, Nagpur', loc: 'Other', cutoff: 75000, seats: 180 }
  ];

  let branchMult = 1.0;
  if (branch === 'AI / Data Science') branchMult = 1.3;
  if (branch === 'ENTC / Electronics') branchMult = 2.5;
  if (branch === 'Electrical / Inst') branchMult = 4.0;
  if (branch === 'Core (Mech/Civil/Chem)') branchMult = 6.5;

  let matches = allColleges.filter(c => location === 'All' || c.loc === location).map(c => {
    const adjCutoff = c.cutoff * branchMult;
    let prob = Math.max(5, Math.min(99, 100 - ((effRank / adjCutoff) * 50) + 15));
    
    let probText = "";
    if (effRank <= adjCutoff) {
      probText = `High yield zone. Expected to consume 1 of the ~${Math.round(c.seats*(category==='Open'?1:0.3))} Category seats securely in Round 1.`;
      prob = Math.max(90, prob);
    } else if (effRank <= adjCutoff * 1.4) {
      probText = `Fighting for final boundary seats. Statistically, ${Math.abs(Math.round(effRank - adjCutoff))} candidates buffer you from the CAP R3 cutoff.`;
    } else {
      probText = `Statistically unlikely in standard rounds. Heavily dependent on institutional spot round vacancies.`;
    }

    let status = 'Safe'; let r1 = 'Allotted'; let r3 = 'Upgraded';
    if (effRank > adjCutoff * 1.5) { status = 'Dream'; r1 = 'Waitlist'; r3 = 'Borderline'; } 
    else if (effRank > adjCutoff * 0.9) { status = 'Target'; r1 = 'Waitlist'; r3 = 'Allotted'; }
    
    return { ...c, status, r1, r3, prob: prob.toFixed(1), probText, adjCutoff, cutoffDiff: Math.abs(effRank - adjCutoff) };
  });

  return matches.sort((a,b) => a.cutoffDiff - b.cutoffDiff).slice(0, 4); 
};

export const generateNeuralAptitude = (math, phy, chem) => {
  if (math >= phy && math >= chem && math > 75) return { title: 'Algorithmic / Computational Logic', desc: 'Your cognitive profile heavily favors abstract logic and pure mathematical structures. Strong aptitude for software architecture.', branches: 'Computer Science, Artificial Intelligence, Data Science' };
  if (phy >= math && phy >= chem && phy > 75) return { title: 'Spatial / Mechanical Engineering', desc: 'You excel at spatial reasoning and real-world physics applications. You visualize physical systems highly efficiently.', branches: 'Mechanical, Robotics, Civil, Aerospace' };
  if (chem >= math && chem >= phy && chem > 75) return { title: 'Process / Chemical Synthesis', desc: 'Your aptitude peaks in structural chemistry and process-oriented memorization. Strong foundation for materials and biomedicine.', branches: 'Chemical, Biotech, Petroleum, Pharmacy' };
  return { title: 'Versatile Analytical Profile', desc: 'Your mastery shape is highly geometric and balanced. You have the flexibility to bridge hardware and software disciplines.', branches: 'Electronics & Telecommunication (ENTC), Instrumentation' };
};

export const getCombatArchetype = (scores) => {
  if (!scores || !scores.math) return { name: "Unknown", desc: "No data available", color: "#6b7280", icon: <Activity size={24} color="#6b7280"/> };
  const totalAttempted = 150 - scores.math.skipped - scores.physics.skipped - scores.chemistry.skipped;
  const totalCorrect = scores.math.correct + scores.physics.correct + scores.chemistry.correct;
  const accuracy = totalAttempted > 0 ? totalCorrect / totalAttempted : 0;
  
  if (accuracy > 0.85) return { name: "The Sniper", desc: "Surgical precision. You only strike when certain. High accuracy, calculated risk.", color: "#10b981", icon: <Crosshair size={24} color="#10b981"/> };
  if (accuracy < 0.55 && totalAttempted > 120) return { name: "The Berserker", desc: "High volume, chaotic energy. You attacked every question relentlessly.", color: "#ef4444", icon: <Flame size={24} color="#ef4444"/> };
  if (scores.chemistry.marks > scores.math.marks && scores.chemistry.marks > scores.physics.marks) return { name: "The Alchemist", desc: "Carried by Chemistry. A master of reactions, theory, and memory retention.", color: "#a855f7", icon: <FlaskConical size={24} color="#a855f7"/> };
  if (scores.math.marks > 60) return { name: "The Architect", desc: "Math carries the load. Heavy structural logic and complex problem-solving.", color: "#3b82f6", icon: <Cpu size={24} color="#3b82f6"/> };
  return { name: "The Tactician", desc: "Calculated, balanced, and highly adaptable across all three subjects.", color: "#eab308", icon: <Target size={24} color="#eab308"/> };
};

export const generateInsight = (scores) => {
  if (!scores || !scores.math) return "Awaiting data extraction...";
  const mAcc = scores.math.totalQs > 0 ? (scores.math.correct / scores.math.totalQs) : 0;
  const pAcc = scores.physics.totalQs > 0 ? (scores.physics.correct / scores.physics.totalQs) : 0;
  const cAcc = scores.chemistry.totalQs > 0 ? (scores.chemistry.correct / scores.chemistry.totalQs) : 0;
  if (mAcc >= pAcc && mAcc >= cAcc && mAcc > 0.5) return `Outstanding Mathematics accuracy (${(mAcc*100).toFixed(0)}%) acts as the primary anchor for your rank velocity.`;
  if (cAcc >= mAcc && cAcc >= pAcc && cAcc > 0.6) return `High yield in Chemistry (${(cAcc*100).toFixed(0)}% accuracy) significantly boosts your aggregate normalization.`;
  if (pAcc >= mAcc && pAcc >= cAcc && pAcc > 0.6) return `Strong Physics performance (${(pAcc*100).toFixed(0)}% accuracy) drives your percentile prediction upward.`;
  if (scores.total < 60) return "Accuracy matrix is highly distributed. Strategic focus on high-weightage topics recommended.";
  return "Well-balanced geometric accuracy profile across Mathematics, Physics, and Chemistry.";
};
